import {Component, inject, OnInit, input, output} from '@angular/core';
import {ReactiveFormsModule} from '@angular/forms';
import {TranslocoPipe} from '@jsverse/transloco';
import {Observable} from 'rxjs';
import {switchMap} from 'rxjs/operators';
import {RichTextEditorComponent} from '../../../../../../../shared/components/rich-text-editor/rich-text-editor.component';
import {
  ProjectCharterParametersResponse,
  ProjectCharterParametersUpsertRequest,
} from '@frontend/shared-api';
import {Step12ProjectCharterService} from '../step-1-2-project-charter.service';
import {
  ProjectStepRichTextService,
  RichTextSection,
  RichTextSectionConfig,
} from '../../../../../../../shared/services/project-step-rich-text.service';
import {TabStatus} from '../../../../../../../shared/tabs/tab-status.model';
import {ProjectBaseStepComponent} from '../../../../project-base-step/project-base-step.component';
import {SECTION_KEYS} from '../../../../../../../shared/models/charter.types';

type ParametersField = keyof ProjectCharterParametersUpsertRequest;

@Component({
  selector: 'app-step-1-2-project-charter-parameters',
  providers: [ProjectStepRichTextService],
  imports: [ReactiveFormsModule, TranslocoPipe, RichTextEditorComponent],
  templateUrl: './step-1-2-project-charter-parameters.component.html',
})
export class Step12ProjectCharterParametersComponent
  extends ProjectBaseStepComponent
  implements OnInit
{
  private readonly chartService = inject(Step12ProjectCharterService);
  readonly richTextService = inject(
    ProjectStepRichTextService<
      ProjectCharterParametersUpsertRequest,
      ProjectCharterParametersResponse
    >,
  );

  readonly preloadedData = input<ProjectCharterParametersResponse>();
  readonly statusChange = output<TabStatus>();

  onStatusChange = () => {
    this.emitStatusChange();
  };

  get parameterSections() {
    return this.richTextService.getSections();
  }

  override ngOnInit(): void {
    super.ngOnInit();
    this.initializeRichTextService();
  }

  private initializeRichTextService(): void {
    const config: RichTextSectionConfig<
      ProjectCharterParametersUpsertRequest,
      ProjectCharterParametersResponse
    > = {
      sections: PARAMETERS_SECTIONS,
      fieldMap: PARAMETERS_FIELD_MAP,
      saveFunction: (projectId, payload) => this.saveWithCompleteDto(projectId, payload),
      loadFunction: (projectId) => this.chartService.getCharterParameters(projectId),
    };

    this.richTextService.initialize(this.projectId, config, this.preloadedData());
  }

  private saveWithCompleteDto(
    projectId: number,
    richTextPayload: ProjectCharterParametersUpsertRequest,
  ): Observable<ProjectCharterParametersResponse> {
    return this.chartService.getCharterParameters(projectId).pipe(
      switchMap((existingData) => {
        const completePayload: ProjectCharterParametersUpsertRequest = {
          ...existingData,
          ...richTextPayload,
        };
        return this.chartService.saveCharterParameters(projectId, completePayload);
      }),
    );
  }

  protected emitStatusChange(): void {
    const totalSections = this.richTextService.getTotalSectionsCount();
    const completedSections = this.richTextService.getCompletedSectionsCount();
    this.statusChange.emit({totalSections, completedSections});
  }
}

export const PARAMETERS_SECTIONS: RichTextSection[] = [
  {
    key: SECTION_KEYS.PROJECT_GOALS,
    content: '',
    tooltipKey: 'project.steps.projectChart.projectGoals',
    hasChanges: false,
  },
  {
    key: SECTION_KEYS.DELIVERABLES,
    content: '',
    tooltipKey: 'project.steps.projectChart.deliverables',
    hasChanges: false,
  },
  {
    key: SECTION_KEYS.POTENTIAL_RISKS,
    content: '',
    tooltipKey: 'project.steps.projectChart.potentialRisks',
    hasChanges: false,
  },
  {
    key: SECTION_KEYS.REASONS_FOR_NEW_HR_SYSTEM,
    content: '',
    tooltipKey: 'project.steps.projectChart.reasonsForNewHrSystem',
    hasChanges: false,
  },
];

export const PARAMETERS_FIELD_MAP: Record<string, ParametersField> = {
  [SECTION_KEYS.PROJECT_GOALS]: 'projectGoals',
  [SECTION_KEYS.DELIVERABLES]: 'deliverables',
  [SECTION_KEYS.POTENTIAL_RISKS]: 'potentialRisks',
  [SECTION_KEYS.REASONS_FOR_NEW_HR_SYSTEM]: 'reasonsForNewHrSystem',
};
